import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useHistory, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from "react-i18next";
import { useFormik } from 'formik';
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types';
import Select from 'react-select';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import "react-datepicker/dist/react-datepicker.css";
import { 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  InputGroup,
  FormText,
  FormFeedback,
  Container,
  Button,
  Col,
  Spinner,
  Card,
  CardBody
} from 'reactstrap';
import * as Yup from 'yup';
import clsx from 'clsx';
import { useField } from 'formik';
import styled from 'styled-components';
import InputWithLoader from '../components/InputWithLoader';
import PhoneNumberInput from '../components/PhoneNumberInput';
import Checkbox from '../components/Checkbox';
import useInterval from '../hooks/useInterval'
import useQueryParams from '../hooks/useQuery'

import { LegalParticipantSchema, IndividualParticipantSchema } from '../components/ParticipantForm'


import { fetchForm, resendUserFormDocument } from '../api/actions/forms';
import { fetcher } from '../api/client';


import Logo from '../images/logo/owl-logo.svg';
import Step1 from '../images/public-form/step1.svg';
import Step2 from '../images/public-form/step2.svg';
import Step3 from '../images/public-form/step3.svg';
import Step4 from '../images/public-form/step4.svg';
import Step5 from '../images/public-form/step5.svg';
import PoweredBy from '../images/logo/poweredby.png';

registerLocale('es', es);

const Root = styled.section`
  font-family: "Lato", sans-serif;
`
const StyledCard = styled(Card)`
  border-radius: 22px;
`
const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 120px;
  height: 120px;
  top: -60px;
  left: 50%;
  background: white;
  overflow: hidden;
  transform: translate(-50%, 0); 
  border-radius: 50%;
`
const Image = styled.img`
  width: 90%;
  ${'' /* height: auto; */}
`
const Bullet = styled.div`
  width: 10px;
  cursor: pointer;
  height: 10px;
  background: ${props => props.isActive ? "#f47b33" : "#344e5b"};
  border-radius: 50%;
  margin: 10px;
`

const getSteps = ({ t }) => [
  {
    title: t('publicForms.welcome'),
    description: t('publicForms.step1'),
    image: Step1,
  },
  {
    title: t('publicForms.step', { number: 1 }),
    description: t('publicForms.step2'),
    image: Step2,
  },
  {
    title: t('publicForms.step', { number: 2 }),
    description: t('publicForms.step3'),
    image: Step3,
  },
  {
    title: t('publicForms.step', { number: 3 }),
    description: t('publicForms.step4'),
    image: Step4,
  },
  {
    title: t('publicForms.step', { number: 4 }),
    description: t('publicForms.step5'),
    image: Step5,
  },
]

function PublicForm() {
    const { t, i18n } = useTranslation('common', { useSuspense: false });

    const { formId, userFormId } = useParams();
    const [status, setStatus] = useState('');

    const steps = useMemo(() => {
      return getSteps({ t })
    }, [t])


    const { data, isLoading, isError, error } = useQuery(['form', formId], () => fetcher(fetchForm(formId, { onlyPreview: true })), {
      onMutate: () => {
        setStatus('loading')
      },
      onSuccess: ({data}) => {
        i18n.changeLanguage(data?.language?.toLowerCase())
      },
      refetchOnWindowFocus: false
    });


    const [currentStep, setCurrentStep] = useState(1);

    const nextStep = () => {
      setCurrentStep(step => Math.min(step + 1, steps.length))
    }
    const goToStep = (number) => {
      setCurrentStep(number)
    }

    const handleCreateUserForm = () => {

    }

    return (
        <PublicFormLayout title={data?.data?.name} image={data?.data?.tenantLogo} isLoading={isLoading} isError={isError} error={error}>
          <div className="text-center p-4">
            {/* {t('publicForms.firstStep')} */}
              <h2 className="font-weight-bold">{steps[currentStep - 1].title}</h2>
              <p className="font-weight-bold">{steps[currentStep - 1].description}</p>
              <div className="p-2">
                <img src={steps[currentStep - 1].image} alt="step1"  style={{height: 200, width: 300}} />
                <div className="d-flex justify-content-center ">
                  {
                    steps.map((step, i) => (
                      <Bullet isActive={currentStep === i + 1} onClick={() => setCurrentStep(i + 1)} />
                    ))
                  }
                </div>
              </div>
              <div className="text-center">
                <Button className="mx-1" color="accent" size="md" onClick={nextStep} style={{width: 180}}>
                  {t('publicForms.startTutorial')}
                </Button>
                <Link to={`/forms/${formId}/create`} component={Button} className="mx-1" color="accent-100" size="md" style={{width: 180}} onClick={handleCreateUserForm}>
                  {t('publicForms.start')}
                </Link>
              </div>
              <div className="mt-2 text-center " >
                <Link to={`/forms/${formId}/recover`} className="text-underline underline-on-hover" >
                    {t('actions.recoverDocument')}
                </Link>
              </div>
          </div>
        </PublicFormLayout>
    );
}

export default PublicForm;


export function PublicFormLayout({ title, image, isLoading, UpperComponent, children, isError, error }) {
  const { t } = useTranslation();
  const { formId, userFormId } = useParams();
  const queryClient = useQueryClient()

  const { mutate: resendDocumentMutate } = useMutation(() => fetcher(resendUserFormDocument(formId, userFormId)), {
    onSuccess: (data) => {
      toast.info(t('successMessages.resentContract', {participant: data.participantEmail}), {autoClose: 3000})
    },
    onError: (err) => {
      toast.error(t(err.message))
    }
  });

  const handleInvalidate = () => {
    queryClient.invalidateQueries(['form', formId])
  }

  const handleResend = () => {
    resendDocumentMutate()
  }

  return (
    <Root className="vh-100 d-flex">
        <Container className="align-self-start my-2">
          <div className="w-100 d-flex flex-column align-items-center justify-content-center py-5 mb-5 font-weight-bold text-uppercase">
            {title}
            {UpperComponent && React.cloneElement(UpperComponent, {})}
          </div>
          
          <StyledCard className="account-success align-self-center mx-auto w-50">
              <CardBody className="p-lg-5 p-3 position-relative">
                  { !isLoading && 
                    <ImageWrapper className="mb-md-3 mb-5" className="shadow" >
                        <Image src={image ?? Logo} className="img-fluid" alt="Trato Logo" />
                    </ImageWrapper> 
                  }
                  {
                    isLoading ? <div className="d-flex justify-content-center mt-3"><Spinner /></div>
                    : isError ? (
                    <div className="my-4 w-100 text-center">
                      <h3 className="font-weight-bold">{t('publicForms.somethingWentWrong')}</h3><br />
                      <div className="text-danger">{t(`errors.${error?.message}`)}</div>
                    </div>
                  ) :
                    <div className="mt-md-0 mt-5 ">
                      {children}
                    </div>
                  }
                  {
                    error?.message === 'UserFormCompleted' && 
                    <div className="mt-4 d-flex justify-content-centeralign-items-center">
                        <Button 
                          color="accent" 
                          tag={Link} 
                          to={`/forms/${formId}/create`}
                          replace
                          onClick={handleInvalidate}
                        >
                             {t('actions.createNewDocument')}
                        </Button>
                        <Button 
                          color="accent"
                          className="ml-2"
                          onClick={handleResend}
                        >
                             {t('actions.resend')}
                        </Button>
                    </div>
                  }
                  {
                    error?.message === 'IncompleteUserFormNotFound' && 
                    <div className="mt-4 d-flex justify-content-center flex-column align-items-center">
                        <Button 
                          color="accent" 
                          tag={Link} 
                          to={`/forms/${formId}/recover`}
                          replace
                        >
                            {t('actions.goBack')}
                        </Button>
                    </div>
                  }

              </CardBody>
          </StyledCard>
          <div className="d-flex justify-content-center mt-2">
            <img src={PoweredBy} alt="powered by trato" width={150} />
          </div>
        </Container>
    </Root>
  )
}
